import { z } from 'zod';
import { dataHoraSchema, paginacaoSchema } from './comum.js';
/**
 * ============================================================================
 * FINANCEIRO — cobranca da Nyroh ao cliente, e do cliente ao cliente dele
 * ============================================================================
 *
 * Sao dois niveis que o codigo antigo misturava:
 *   `pagamentos`  — a Nyroh cobrando do dono do site (Stripe / Pix).
 *   `faturas`     — o dono do site cobrando o cliente final dele.
 *
 * As faturas e os perfis do dossie viviam em localStorage (`nyroh_customers_*`,
 * `nyroh_invoices_*`). Isso significava que o dado sumia ao trocar de navegador e
 * nao existia para ninguem alem de quem estava com a aba aberta. Agora sao tabelas.
 */
export const metodoPagamentoSchema = z.enum(['card', 'pix_auto', 'pix_manual']);
export const statusPagamentoSchema = z.enum(['pending', 'paid', 'failed', 'verifying']);
export const pagamentoSchema = z.object({
    id: z.uuid(),
    clienteId: z.uuid(),
    valor: z.number(),
    metodo: metodoPagamentoSchema,
    status: statusPagamentoSchema,
    stripePaymentIntentId: z.string().nullable(),
    urlComprovante: z.string().nullable(),
    criadoEm: dataHoraSchema,
    atualizadoEm: dataHoraSchema.nullable(),
});
export const criarPagamentoSchema = pagamentoSchema.omit({
    id: true,
    criadoEm: true,
    atualizadoEm: true,
});
export const atualizarPagamentoSchema = z.object({
    status: statusPagamentoSchema,
    urlComprovante: z.string().optional(),
});
export const configPagamentoClienteSchema = z.object({
    clienteId: z.uuid(),
    tipoChavePix: z.enum(['cnpj', 'cpf', 'email', 'phone', 'random']).nullable(),
    chavePix: z.string().nullable(),
    nomeRecebedorPix: z.string().nullable(),
    cidadePix: z.string().nullable(),
    linkPagamentoCartao: z.string().nullable(),
    instrucoesLinkCartao: z.string().nullable(),
    provedorGateway: z.enum(['mercadopago', 'asaas', 'stripe', 'none']).nullable(),
    /** Nunca devolvido pela API — so gravado. A leitura devolve `gatewayConfigurado`. */
    gatewayToken: z.string().nullable().optional(),
    gatewayConfigurado: z.boolean().default(false),
    gatewayAtivo: z.boolean().default(false),
    permitePixDireto: z.boolean().default(true),
    permiteLinkCartao: z.boolean().default(false),
    atualizadoEm: dataHoraSchema.nullable(),
});
export const salvarConfigPagamentoSchema = configPagamentoClienteSchema
    .omit({ clienteId: true, atualizadoEm: true, gatewayConfigurado: true })
    .partial();
export const statusFaturaSchema = z.enum(['pending', 'paid', 'expired', 'canceled']);
export const faturaSchema = z.object({
    id: z.uuid(),
    numero: z.string(),
    clienteId: z.uuid(),
    clienteSlug: z.string().nullable(),
    telefoneConsumidor: z.string(),
    nomeConsumidor: z.string(),
    emailConsumidor: z.email().nullable(),
    titulo: z.string(),
    descricao: z.string().nullable(),
    valor: z.number(),
    vencimento: z.string(),
    status: statusFaturaSchema,
    tipoMetodo: z.enum(['pix', 'card', 'gateway']).nullable(),
    codigoPix: z.string().nullable(),
    urlPagamentoCartao: z.string().nullable(),
    pagoEm: dataHoraSchema.nullable(),
    urlComprovante: z.string().nullable(),
    criadoEm: dataHoraSchema,
});
export const criarFaturaSchema = faturaSchema.omit({
    id: true,
    numero: true,
    criadoEm: true,
    pagoEm: true,
    status: true,
});
export const pagarFaturaSchema = z.object({
    metodo: z.enum(['pix', 'card', 'gateway']).default('pix'),
    urlComprovante: z.string().optional(),
});
export const filtroFaturasSchema = paginacaoSchema.extend({
    telefoneConsumidor: z.string().optional(),
    status: statusFaturaSchema.optional(),
});
/**
 * Dossie 360 do consumidor final. Antes era mock em localStorage com quatro
 * pessoas fixas; agora e tabela por cliente.
 */
export const servicoConsumidorSchema = z.object({
    id: z.string(),
    titulo: z.string(),
    valor: z.number(),
    data: z.string(),
    status: z.enum(['pago', 'pendente', 'cancelado']),
    categoria: z.string().optional(),
});
export const eventoLinhaTempoSchema = z.object({
    id: z.string(),
    tipo: z.enum([
        'lead_captured',
        'booking_created',
        'invoice_issued',
        'payment_received',
        'portal_accessed',
        'note',
    ]),
    titulo: z.string(),
    descricao: z.string(),
    quando: dataHoraSchema,
    valor: z.number().optional(),
});
export const perfilConsumidorSchema = z.object({
    id: z.uuid(),
    clienteId: z.uuid(),
    nome: z.string(),
    telefone: z.string(),
    email: z.email().nullable(),
    tipo: z.enum(['lead', 'cliente']),
    status: z.string(),
    criadoEm: dataHoraSchema,
    ultimaAtividadeEm: dataHoraSchema.nullable(),
    receitaTotal: z.number().default(0),
    pendenteTotal: z.number().default(0),
    servicos: z.array(servicoConsumidorSchema).default([]),
    linhaTempo: z.array(eventoLinhaTempoSchema).default([]),
    observacoes: z.string().nullable(),
    codigoAcessoPortal: z.string().nullable(),
    responsavel: z.string().nullable(),
    origem: z.string().nullable(),
});
export const salvarPerfilConsumidorSchema = perfilConsumidorSchema
    .omit({ criadoEm: true, ultimaAtividadeEm: true })
    .partial({ id: true });
export const configGlobalSchema = z.object({
    precoPadraoSite: z.number(),
    pixCnpj: z.string().nullable(),
    dominioBaseSites: z.string(),
    atualizadoEm: dataHoraSchema.nullable(),
});
export const salvarConfigGlobalSchema = configGlobalSchema
    .omit({ atualizadoEm: true })
    .partial();
//# sourceMappingURL=financeiro.js.map